import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, App, Button, Card, Collapse, Descriptions, Divider, Form, Input, Popconfirm,
  Space, Spin, Tag, Tooltip, Typography,
} from 'antd';
import {
  CopyOutlined, EyeInvisibleOutlined, EyeOutlined, LockOutlined,
  LogoutOutlined, ReadOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  changePassword, logoutLocal, logoutPassgate, refreshLocal, refreshPassgateTokens,
} from '@/apis/auth';
import { fetchMyBindings, myUnbindFeishu, type MyBindings } from '@/apis/me';
import { resetCurrentUserCache, useCurrentUser } from '@/common/auth/adminAcl';
import {
  LocalTokens, clearAllTokens, getActiveAccessToken, getActiveRefreshToken,
  getCurrentLoginKind, getTokenExpiresAt, setLoginTokens, type LoginKind,
} from '@/common/auth/tokens';

const { Title, Text, Paragraph } = Typography;

interface PwdForm { oldPassword: string; newPassword: string; confirm: string }

const formatTime = (v?: string | null) =>
  (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-');

/** 把毫秒时间戳格式化成"剩余 1h2m / 已过期"；无效输入返回 '-'。 */
const formatExpiresIn = (expiresAt: number | null): string => {
  if (!expiresAt) return '-';
  const diffMs = expiresAt - Date.now();
  if (diffMs <= 0) return '已过期';
  const totalSec = Math.floor(diffMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `剩余 ${h}h${m}m`;
  if (m > 0) return `剩余 ${m}m${s}s`;
  return `剩余 ${s}s`;
};

interface TokenRowProps {
  label: string;
  token: string | null;
  expiresAt?: number | null;
  helpText?: string;
}

/** 单行 Token 展示：默认遮蔽 → 一键复制 → 可选剩余有效期。
 *
 * 注意：原来用 ``Descriptions.Item`` 作为返回根节点会被外层 ``<Descriptions>`` 漏渲染——
 * AntD 通过 ``React.Children.map`` 找的是 ``Descriptions.Item`` 节点，自定义壳组件
 * 不被识别，结果只渲染 label，content 整段丢失。改成纯 Row + label 自渲染。
 */
function TokenRow({ label, token, expiresAt, helpText }: TokenRowProps) {
  const { message } = App.useApp();
  const [visible, setVisible] = useState(false);

  const handleCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      message.success(`${label} 已复制`);
    } catch {
      message.error('复制失败，请手动选中文本复制');
    }
  };

  // 隐藏态：用圆点等长替代，避免泄露真实长度同时保证一定有可见字符
  const displayValue = token
    ? (visible ? token : '•'.repeat(Math.min(token.length, 48)))
    : '';

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', padding: '8px 0',
      borderBottom: '1px solid #f0f0f0',
    }}>
      <div style={{ width: 140, color: 'rgba(0,0,0,0.65)', flexShrink: 0, paddingTop: 6 }}>
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {token ? (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={displayValue}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: 12 }}
                onFocus={(e) => visible && e.currentTarget.select()}
              />
              <Tooltip title={visible ? '隐藏' : '显示'}>
                <Button
                  icon={visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  onClick={() => setVisible((v) => !v)}
                />
              </Tooltip>
              <Tooltip title="复制">
                <Button icon={<CopyOutlined />} onClick={handleCopy} />
              </Tooltip>
            </Space.Compact>
            {expiresAt !== undefined && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatExpiresIn(expiresAt ?? null)}
                {expiresAt ? `（${dayjs(expiresAt).format('YYYY-MM-DD HH:mm:ss')}）` : ''}
              </Text>
            )}
            {helpText && (
              <Text type="secondary" style={{ fontSize: 12 }}>{helpText}</Text>
            )}
          </Space>
        ) : (
          <Text type="secondary">未签发</Text>
        )}
      </div>
    </div>
  );
}

const CURL_EXAMPLE = (apiBase: string) =>
  `curl -H "Authorization: Bearer <ACCESS_TOKEN>" \\\n  ${apiBase || 'https://<your-host>'}/auth/me`;

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const { me, loading } = useCurrentUser();
  const [bindings, setBindings] = useState<MyBindings | null>(null);
  const [bLoading, setBLoading] = useState(false);
  const [pwdForm] = Form.useForm<PwdForm>();
  const [submitting, setSubmitting] = useState(false);

  // Token 卡片：每秒从 localStorage 派生，避免 axios 拦截器静默 refresh 后 UI 不更新
  const [tokenTick, setTokenTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTokenTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const loginKind: LoginKind = useMemo(() => getCurrentLoginKind(), [tokenTick]);
  const accessToken = useMemo(() => getActiveAccessToken(), [tokenTick]);
  const refreshTokenStr = useMemo(() => getActiveRefreshToken(), [tokenTick]);
  const accessExpiresAt = useMemo(() => getTokenExpiresAt(accessToken), [accessToken]);
  const [tokenRefreshing, setTokenRefreshing] = useState(false);

  const handleRefreshToken = async () => {
    if (!refreshTokenStr) {
      message.warning('当前没有 refresh token，请重新登录后再试');
      return;
    }
    setTokenRefreshing(true);
    try {
      if (loginKind === 'local') {
        const res = await refreshLocal(refreshTokenStr);
        if (!res?.accessToken) {
          message.error('刷新失败，请重新登录');
          return;
        }
        setLoginTokens('local', res.accessToken, res.refreshToken);
      } else if (loginKind === 'passgate') {
        const data = (await refreshPassgateTokens(refreshTokenStr)) as
          { access_token?: string; refresh_token?: string } | null;
        const newAccess = data?.access_token;
        const newRefresh = data?.refresh_token;
        if (!newAccess) {
          message.error('刷新失败，请重新登录');
          return;
        }
        setLoginTokens('passgate', newAccess, newRefresh);
      } else {
        message.warning('未识别登录方式');
        return;
      }
      setTokenTick((n) => n + 1); // 立即重读 localStorage，不等下一拍
      message.success('Token 已刷新');
    } finally {
      setTokenRefreshing(false);
    }
  };

  /**
   * 打开接口文档：`/api/docs` 挂着 FastAPI Swagger UI，接口本身要 Bearer 鉴权，
   * 直接开新 tab 无法带 Authorization header。做法是让后端 _extract_bearer
   * 兼容 query 参数 ``?authorization=<token>``，前端直接 window.open 到该 URL。
   *
   * 好处：新 tab 打开的是**真同域** /api/docs，Swagger UI 相对路径的 static
   * asset（swagger-ui-bundle.js 等）都能正确加载；缺点是 URL 里带 token 字面量，
   * 浏览器 history / 服务器 access log 可能留痕，仅供内部登录用户使用。
   *
   * 用户点 Try it out 想调具体接口时仍需在 Swagger UI 顶部 Authorize 面板贴一次
   * Bearer token —— Swagger UI 内部 fetch 默认不从 URL 继承 token。
   */
  const handleOpenApiDocs = () => {
    if (!accessToken) {
      message.warning('未获取到 Access Token，请先登录或刷新');
      return;
    }
    const url = `/api/docs?authorization=${encodeURIComponent(accessToken)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const reload = useCallback(async () => {
    setBLoading(true);
    try {
      setBindings(await fetchMyBindings());
    } finally {
      setBLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleChangePwd = async () => {
    const v = await pwdForm.validateFields();
    if (v.newPassword !== v.confirm) {
      message.error('两次新密码不一致');
      return;
    }
    setSubmitting(true);
    try {
      const ok = await changePassword(v.oldPassword, v.newPassword);
      if (!ok) return; // axios 拦截器已弹错
      message.success('密码已修改，请使用新密码重新登录');
      // 改密会 bump perms_ver；老 access 立即失效，本地登出最稳
      await logoutLocal(LocalTokens.getRefresh());
      clearAllTokens();
      resetCurrentUserCache();
      navigate('/user-login', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnbindFeishu = async () => {
    const ok = await myUnbindFeishu();
    if (ok) {
      message.success('已解绑飞书');
      reload();
    }
  };

  const handleLogout = async () => {
    const kind = getCurrentLoginKind();
    if (kind === 'local') await logoutLocal(LocalTokens.getRefresh());
    else if (kind === 'passgate') await logoutPassgate();
    clearAllTokens();
    resetCurrentUserCache();
    navigate('/user-login', { replace: true });
  };

  if (loading || !me) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 16, background: '#fff', minHeight: '100%' }}>
      <Title level={4} style={{ marginTop: 0 }}>个人设置</Title>

      <Card title="基础信息" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="姓名">{me.user.name}</Descriptions.Item>
          <Descriptions.Item label="邮箱">{me.user.email}</Descriptions.Item>
          <Descriptions.Item label="登录用户名">{me.user.username || '-'}</Descriptions.Item>
          <Descriptions.Item label="账户来源">{me.user.source}</Descriptions.Item>
          <Descriptions.Item label="角色" span={2}>
            {me.roles.length === 0 ? <Tag>无</Tag> : me.roles.map((r) => (
              <Tag color="green" key={r}>{r}</Tag>
            ))}
          </Descriptions.Item>
          <Descriptions.Item label="权限点数" span={2}>
            <Text type="secondary">{me.permissions.length} 项（详见后台管理）</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="登录方式 / 绑定" style={{ marginBottom: 16 }} loading={bLoading}>
        {bindings ? (
          <>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={<><Tag color="blue">本地</Tag>账号密码</>}>
                {bindings.local.bound ? (
                  <Space>
                    <Text>已绑定（用户名：{bindings.local.username || '-'}）</Text>
                    <Text type="secondary">绑定于 {formatTime(bindings.local.boundAt)}</Text>
                  </Space>
                ) : (
                  <Text type="secondary">未绑定本地登录（请联系管理员重置密码以补绑）</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={<><Tag color="purple">飞书</Tag>Passgate</>}>
                {bindings.feishu.bound ? (
                  <Space>
                    <Text>已绑定（{bindings.feishu.email || '-'}）</Text>
                    <Text type="secondary">绑定于 {formatTime(bindings.feishu.boundAt)}</Text>
                    <Popconfirm
                      title="解绑飞书后将无法用飞书登录该账号"
                      description="后端要求至少保留一种登录方式，未绑本地时会被拒绝。"
                      onConfirm={handleUnbindFeishu}
                      okText="确认解绑"
                      okButtonProps={{ danger: true }}
                      disabled={!bindings.local.bound}
                    >
                      <Button danger size="small" disabled={!bindings.local.bound}>解绑</Button>
                    </Popconfirm>
                  </Space>
                ) : (
                  <Text type="secondary">未绑定飞书登录</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
            {!bindings.local.bound && bindings.feishu.bound && (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                message="尚未绑定本地账号密码登录方式，无法解绑飞书。"
                description="请联系管理员通过『后台管理 → 用户管理 → 补绑本地』给您绑定一份本地登录。"
              />
            )}
          </>
        ) : null}
      </Card>

      <Card title="修改密码" style={{ marginBottom: 16 }}>
        {bindings?.local.bound ? (
          <Form form={pwdForm} layout="vertical" style={{ maxWidth: 480 }}>
            <Form.Item label="原密码" name="oldPassword" rules={[{ required: true }]}>
              <Input.Password prefix={<LockOutlined />} />
            </Form.Item>
            <Form.Item
              label="新密码"
              name="newPassword"
              rules={[
                { required: true },
                { min: 8, message: '至少 8 位' },
                { pattern: /[A-Za-z]/, message: '需含字母' },
                { pattern: /\d/, message: '需含数字' },
              ]}
            >
              <Input.Password prefix={<LockOutlined />} />
            </Form.Item>
            <Form.Item
              label="再次输入新密码"
              name="confirm"
              dependencies={['newPassword']}
              rules={[{ required: true }]}
            >
              <Input.Password prefix={<LockOutlined />} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" loading={submitting} onClick={handleChangePwd}>
                提交并重新登录
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Text type="secondary">仅飞书绑定的账号需先通过管理员补绑本地登录方式后才能修改密码。</Text>
        )}
      </Card>

      <Card
        title="API 凭证（Token）"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Tag color={loginKind === 'local' ? 'blue' : loginKind === 'passgate' ? 'purple' : 'default'}>
              {loginKind === 'local' ? '本地登录' : loginKind === 'passgate' ? '飞书登录' : '未登录'}
            </Tag>
            <Tooltip title="用当前 Access Token 打开 Swagger UI 接口文档；Try it out 自动带鉴权">
              <Button
                size="small"
                icon={<ReadOutlined />}
                onClick={handleOpenApiDocs}
                disabled={!accessToken}
              >
                接口文档
              </Button>
            </Tooltip>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={tokenRefreshing}
              onClick={handleRefreshToken}
              disabled={!refreshTokenStr}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Token 等同于您的账号身份，切勿粘贴到聊天群、公开仓库或截图分享。"
        />
        <TokenRow
          label="Access Token"
          token={accessToken}
          expiresAt={accessExpiresAt}
          helpText="放在请求头：Authorization: Bearer <token>；过期后点右上『刷新』即可"
        />
        <Collapse
          ghost
          style={{ marginTop: 8 }}
          items={[{
            key: 'usage',
            label: '使用示例（curl / Python）',
            children: (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Paragraph style={{ marginBottom: 4 }}>
                  <Text type="secondary">curl 调用 /auth/me 验证 token：</Text>
                </Paragraph>
                <pre style={{
                  background: '#f5f5f5', padding: 12, borderRadius: 4, margin: 0,
                  fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                  {CURL_EXAMPLE(import.meta.env.VITE_SIM_PLATFORM_API_URL || '')}
                </pre>
                <Paragraph style={{ marginBottom: 4, marginTop: 8 }}>
                  <Text type="secondary">Python（httpx）：</Text>
                </Paragraph>
                <pre style={{
                  background: '#f5f5f5', padding: 12, borderRadius: 4, margin: 0,
                  fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                  {`import httpx\nresp = httpx.get(\n  "${import.meta.env.VITE_SIM_PLATFORM_API_URL || 'https://<your-host>'}/auth/me",\n  headers={"Authorization": f"Bearer {ACCESS_TOKEN}"},\n  timeout=10,\n)\nresp.raise_for_status()`}
                </pre>
              </Space>
            ),
          }]}
        />
      </Card>

      <Divider />
      <Space>
        <Button danger icon={<LogoutOutlined />} onClick={() => modal.confirm({
          title: '确认登出？',
          onOk: handleLogout,
        })}>
          登出当前账号
        </Button>
      </Space>
    </div>
  );
}
